import { useEffect, useMemo, useState } from 'react'
import dayjs from 'utils/dayjs'
import { Container } from 'react-grid-system'
import { Button, Heading, JsonLD } from '~elements'
import { useCalendar } from '../Calendar/hooks/useCalendar'
import EventsList from './EventsList/EventsList'
import { createEventStructuredData } from './utils'
import { EventFormatter } from '../Calendar/utils/EventFormatter'
import { EventPaginatedService } from '../Calendar/services/EventPaginatedService'
import styles from './CalendarEDP.module.scss'
import type { CalendarEdpProps } from '~types'
import type { EventsByDate, EventsByMonth } from '../Calendar/types/Event'
import { CALENDAR_EDP_EVENTS_LIMIT, CALENDAR_EDP_NUMBER_OF_MONTHS } from '../Calendar/constants'

const CalendarEDP = ({
  calendarId,
  heading,
  ctaText,
  soldOutConfirmationModalText,
  hidePricing,
  overrideApiSoldOutStatus,
  seo,
  artist
}: CalendarEdpProps) => {
  const startDate = useMemo(() => dayjs(), [])
  const endDate = dayjs().add(CALENDAR_EDP_NUMBER_OF_MONTHS, 'months')

  const [page, setPage] = useState(1)
  const [pageCount, setPageCount] = useState(1)
  const [eventsData, setEventsData] = useState<EventsByDate>()
  const [eventsByMonth, setEventsByMonth] = useState<EventsByMonth>({})
  const [paginatedEvents, setPaginatedEvents] = useState<string[]>([])
  const [jsonLdData, setJsonLdData] = useState({})

  const { filteredItems, selectedMonth } = useCalendar({
    initialDate: startDate,
    endDate,
    artistIds: artist?.artistId,
    isTablet: false
  })

  const eventFormatter = useMemo(() => new EventFormatter(), [])

  useEffect(() => {
    if (!filteredItems?.length) return

    const { eventsByDate, eventsByMonth } = eventFormatter.processEventEngineResponse(filteredItems)
    setEventsData(eventsByDate)
    setEventsByMonth(eventsByMonth)

    const paginationService = new EventPaginatedService(eventsByDate, page, CALENDAR_EDP_EVENTS_LIMIT)
    setPaginatedEvents(paginationService.paginatedEvents)
    setPageCount(paginationService.totalPageCount)

    const structuredData = createEventStructuredData(seo, filteredItems, hidePricing)
    setJsonLdData(structuredData ?? {})
  }, [filteredItems, seo, hidePricing, page, eventFormatter])

  const loadMore = () => setPage(prev => prev + 1)

  return (
    <>
      <JsonLD data={jsonLdData} />
      <Container className='container--width-100'>
        <div className={styles.calendar} id={calendarId}>
          {heading && <Heading level={2}>{heading}</Heading>}

          <div className={styles['calendar-wrapper']}>
            {eventsData && (
              <EventsList
                filteredCalendarData={eventsData}
                eventsByMonth={eventsByMonth}
                paginatedEventsByDate={paginatedEvents}
                hidePricing={hidePricing}
                overrideApiSoldOutStatus={overrideApiSoldOutStatus}
                modalData={{
                  ctaBuyText: ctaText,
                  ctaSoldOutText: soldOutConfirmationModalText,
                  showPriceInModal: !hidePricing
                }}
              />
            )}
          </div>

          {pageCount > 1 && page < pageCount && (
            <div className={styles['load-more']}>
              <Button color='light' onClick={loadMore} title='Load More' analytics={{ skipEvent: true }} />
            </div>
          )}
        </div>
      </Container>
    </>
  )
}

export default CalendarEDP
